// What a material's save sends for its declared units, and when the save has to be refused.
//
// Kept out of MaterialDetailView.vue so the rule is decidable — and testable — from plain data: the
// material's declared-unit rows plus whether it is mapped.
//
// A mapping's emission math scales the EPD by the material's PREFERRED declared unit, so a mapped
// material saved without one maps against nothing — which is why the save refuses it (RTLCA-4887).
// An UNMAPPED material has no EPD to scale: which unit it will end up declared in is a property of
// the EPD it later gets mapped to, so the user cannot know it yet. Requiring one there blocked
// every save on the inventory modal — Speckle imports arrive with `preferred: false` on every row,
// so an unmapped material has no preferred unit at all and lifetime, BIM code, stage and
// description were unsaveable behind a unit error (RTLCA-5673). The requirement now binds a mapped
// material only.
import type { speckleDeclaredUnit } from "@/types/Views/MappingWizard/SpeckleMaterial";

/** The `declaredUnitRequest` shape the API binds (`CreateOrUpdateDeclaredUnitRequest`). */
export interface DeclaredUnitRequest {
  declaredUnit: number;
  value: number;
  preferred: boolean;
}

export interface DeclaredUnitSave {
  /** True when the save must be refused and the inline unit error shown instead. */
  blocked: boolean;
  /** One request per declared unit to write. Empty when there is nothing to write. */
  requests: DeclaredUnitRequest[];
}

// `unit.value` is the live quantity — UnitTable's Quantity input writes edits straight into it,
// leaving `newValue` (the old two-column table's field) untouched.
const toRequest = (unit: speckleDeclaredUnit, preferred: boolean): DeclaredUnitRequest => ({
  declaredUnit: unit.declaredUnit,
  value: unit.value ?? 0,
  preferred,
});

export function resolveDeclaredUnitSave(input: {
  units: speckleDeclaredUnit[] | null | undefined;
  /** `SpeckleMaterial.mapping !== null` — whether an EPD is mapped to this material. */
  isMapped: boolean;
}): DeclaredUnitSave {
  const units = input.units ?? [];
  const preferred = units.find((unit) => unit.preferred);

  if (preferred) return { blocked: false, requests: [toRequest(preferred, true)] };

  if (input.isMapped) return { blocked: true, requests: [] };

  // Nothing to record as a preference, so the only units worth writing are the ones whose quantity
  // the user actually typed. They go with `preferred: false`: the API's `AddOrUpdateDeclaredUnit`
  // leaves any existing preference alone for those, so the edit survives the save without the form
  // inventing a preference nobody expressed. Without this the newly-allowed save would report
  // success and drop the number.
  return { blocked: false, requests: units.filter((unit) => unit.wasChanged).map((unit) => toRequest(unit, false)) };
}
